import React, { useEffect, useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { ModalBody } from 'react-bootstrap';

import type { IDatabaseItemModalConfig, IDeleteDatabaseItemModalConfig } from '@/hooks/types';
import type { IResourceUpsertDatabaseItemDispatch } from '@/store/types/resourceSlice';
import type { IResourceClientMember, IResourceDatabaseField, IResourceDatabaseRecord } from '@/api/types';
import type { IFileProxy } from '@/components/common/form/types';
import type { RootState } from '@/store';
import type { ISelectedFolder } from '@/store/types';

import { selectDatabaseFieldTypeDict, selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { useAppDispatch, useAppSelector, useResourceNavigation, useTimezone } from '@/hooks';
import { DatabaseItemForm } from './DatabaseItemForm';
import { ResourceModalType } from '@/hooks/enums';
import { getMemberList, upsertDatabaseItem } from '@/store/slices/resourcesSlice';
import { ResourceDatabaseFieldTypeId } from '@/api/enums';
import { combineDateTimeToISO } from '@/helpers';
import { ATTACHMENT_NAME_REGEX } from '@/config/variables';
import { selectAuthMenuType } from '@/store/selectors/authSelectors';
import { combineClientMemberList, combineFieldsFromSelectedView } from '@/store/combiners/resourcesCombiners';

interface IFieldsData {
  record: Record<string, string | number | File | null>;
  files: Record<string, File | null>;
}

const transformDatabaseItemFormData = (
  data: Record<string, string | number | IFileProxy[] | null>,
  fields: IResourceDatabaseField[],
  timezoneName: string,
): IFieldsData =>
  fields.reduce<IFieldsData>(
    (transformed, field) => {
      if (field.isInternal && field.name !== 'Created by') return transformed;

      if (field.name === 'Created by') {
        // On create, created by is automatically set to the creating user
        if (!data[field.columnName]) {
          return transformed;
        }

        // On update, create can be manually set with column name coming from properties (instead of auto generated C-value)
        return {
          ...transformed,
          record: { ...transformed.record, [field.properties]: data[field.columnName] as number },
        };
      }

      switch (field.rtxtype_Id) {
        case ResourceDatabaseFieldTypeId.FileAttachment: {
          const fileProxy = data[field.columnName] as IFileProxy[];

          if (fileProxy.length === 0) {
            return {
              ...transformed,
              record: { ...transformed.record, [field.columnName]: '' },
            };
          }

          const file = fileProxy[0]?.file;

          if (!file) {
            return transformed;
          }

          const nameParts = file.name.split('.');
          const fileExtension = nameParts.pop();

          if (!fileExtension) {
            return transformed;
          }

          const fileName = nameParts.join('.');
          const safeName = fileName.replace(ATTACHMENT_NAME_REGEX, '');
          const safeExtension = fileExtension.replace(ATTACHMENT_NAME_REGEX, '');
          const safeFileName = `${safeName}.${safeExtension}`;

          const safeFile = new File([file], safeFileName, { type: file.type, lastModified: file.lastModified });

          return { ...transformed, files: { ...transformed.files, [field.columnName]: safeFile } };
        }
        case ResourceDatabaseFieldTypeId.DateTime: {
          return {
            ...transformed,
            record: {
              ...transformed.record,
              [field.columnName]:
                data[`${field.columnName}-date`] && data[`${field.columnName}-time`]
                  ? combineDateTimeToISO(
                      data[`${field.columnName}-date`] as string,
                      data[`${field.columnName}-time`] as string,
                      timezoneName,
                    )
                  : '',
            },
          };
        }
        case ResourceDatabaseFieldTypeId.Checkbox: {
          return {
            ...transformed,
            record: {
              ...transformed.record,
              [field.columnName]: Number(data[field.columnName]),
            },
          };
        }
        case ResourceDatabaseFieldTypeId.Calculated: {
          return transformed;
        }
        default:
          return {
            ...transformed,
            record: {
              ...transformed.record,
              [field.columnName]: data[field.columnName] as unknown as string | number | null,
            },
          };
      }
    },
    { record: {}, files: {} },
  );

const addMissingMembers = (
  members: IResourceClientMember[],
  resource: IResourceDatabaseRecord,
  fields: IResourceDatabaseField[],
): IResourceClientMember[] => {
  const allMembers = [...members];

  fields.forEach(field => {
    if (field.rtxtype_Id !== ResourceDatabaseFieldTypeId.MemberList) return;

    const [rawMemberId, memberName] = (resource[field.name] as string)?.split('|');
    const memberId = Number(rawMemberId);

    if (!memberId || allMembers.some(member => member.userId === memberId)) return;

    allMembers.push({ userId: memberId, fullName: memberName });
  });

  return allMembers;
};

const resourceModalSelector = createSelector(
  selectFoldersList,
  selectDatabaseFieldTypeDict,
  selectAuthMenuType,
  (_: RootState, selectedFolder: ISelectedFolder) => selectedFolder,
  (foldersList, fieldTypeDict, userType, selectedFolder) => ({
    foldersList,
    fieldTypeDict,
    userType,
    memberList: combineClientMemberList(foldersList, selectedFolder),
    fields: combineFieldsFromSelectedView(foldersList, selectedFolder),
  }),
);

interface IResourceModalProps {
  modalConfig: Exclude<IDatabaseItemModalConfig, IDeleteDatabaseItemModalConfig>;
  handleClose: () => void;
}

const DatabaseItemModal = ({ modalConfig, handleClose }: IResourceModalProps): React.ReactElement => {
  const { selectedFolder } = useResourceNavigation();
  const { foldersList, fieldTypeDict, memberList, userType, fields } = useAppSelector(state =>
    resourceModalSelector(state, selectedFolder),
  );
  const { timezoneName } = useTimezone();
  const dispatch = useAppDispatch();
  const createdByColumnName = useMemo(() => fields.find(field => field.name === 'Created by')?.columnName, [fields]);

  useEffect(() => {
    if (memberList.length === 0) {
      const memberListRequest = dispatch(getMemberList({ clientId: modalConfig.data.clientId }));

      return () => memberListRequest.abort();
    }

    return;
  }, [dispatch, memberList.length, modalConfig.data.clientId]);

  const memberListWithMissingUsers = useMemo(
    () =>
      modalConfig.modalType === ResourceModalType.edit
        ? addMissingMembers(memberList, modalConfig.data.existingResource, fields)
        : memberList,
    [fields, memberList, modalConfig],
  );

  const handleSubmit = (data: Record<string, string | number | null>) => {
    handleClose();

    const fields = modalConfig.data.view.props.map(prop => prop.field);
    const { record, files } = transformDatabaseItemFormData(data, fields, timezoneName);

    const { files: parsedFiles, fileColumnIds } = Object.entries(files).reduce<{
      files: File[];
      fileColumnIds: string[];
    }>(
      (parsed, file) => {
        if (file[1]) {
          return { fileColumnIds: [...parsed.fileColumnIds, file[0]], files: [...parsed.files, file[1] as File] };
        }

        return parsed;
      },
      { files: [], fileColumnIds: [] },
    );

    const payload: IResourceUpsertDatabaseItemDispatch = {
      clientId: modalConfig.data.clientId,
      databaseId: modalConfig.data.databaseId,
      record: JSON.stringify(record),
      viewId: modalConfig.data.view.id,
      files: parsedFiles,
      fileColumnIds,
    };

    if (modalConfig.modalType === ResourceModalType.edit) {
      payload.itemId = modalConfig.data.existingResource.Id;
    }

    dispatch(upsertDatabaseItem(payload));
  };

  if (!foldersList) return <></>;

  return (
    <ModalBody>
      <DatabaseItemForm
        onSubmit={handleSubmit}
        onCancel={handleClose}
        fieldProps={modalConfig.data.view.props}
        foldersList={foldersList}
        createdByColumnName={createdByColumnName}
        existingResource={
          modalConfig.modalType === ResourceModalType.edit ? modalConfig.data.existingResource : undefined
        }
        fieldTypeDict={fieldTypeDict}
        memberList={memberListWithMissingUsers}
        userType={userType}
      />
    </ModalBody>
  );
};

export { DatabaseItemModal };
